<template>
  <div class="page page-full clients-page">
    <div class="page-header with-actions">
      <h1>{{ isPatient ? 'My Reports' : 'Reports' }} </h1>
      <div v-if="isPatient && reportsStats" class="stats">
        <ul>
          <li>
            <span class="text-dark">{{this.reportsStats.completed + this.reportsStats.inProcess + this.reportsStats.cancelled}}</span> REPORTS
          </li>
          <li>
            <span class="text-warning">{{this.reportsStats.inProcess}}</span> IN PROCESS
          </li>
          <li>
            <span class="text-success">{{this.reportsStats.completed}}</span> COMPLETED
          </li>
          <li>
            <span class="text-danger">{{this.reportsStats.cancelled}}</span> CANCELLED
          </li>
        </ul>
      </div>
      <div class="actions" v-if="isClient || isAdmin || isClientAdmin">
        <b-btn @click="createReport" variant="success">Order Analysis</b-btn>
      </div>
    </div>

    <dt-filters-group :tableId="tableId">
      <div class="row">
        <div class="col-12 col-md-6 col-lg-3" v-if="!isPatient">
          <v-form-group :staticLabel="true" icon="magnifier" label="Order ID">
            <dt-filter-input id="order-id-filter"
                             column-name="Id"
                             placeholder="Order ID" />
          </v-form-group>
        </div>
        <div class="col-12 col-md-6 col-lg-3" v-if="!isPatient">
          <v-form-group :staticLabel="true" icon="magnifier" label="Search">
            <dt-filter-input id="patient-name-filter"
                             column-name="PatientName"
                             placeholder="First or Last Name" />
          </v-form-group>
        </div>
        <div class="col-12 col-md-6" :class="{'col-lg-3': !isPatient, 'col-lg-4': isPatient}">
          <v-form-group :staticLabel="true" icon="calendar" label="Date of Order">
            <dt-filter-daterange id="creation-date-filter"
                                 column-name="CreationDate"
                                 placeholder="Any" />
          </v-form-group>
        </div>
        <div class="col-12 col-md-6" :class="{'col-lg-3': !isPatient, 'col-lg-4': isPatient}">
          <v-form-group :staticLabel="true" label="Type">
            <dt-filter-select id="type-filter"
                              column-name="Type"
                              add-empty
                              :options="typeFilterOptions"
                              placeholder="Type of order" />
          </v-form-group>
        </div>
        <div class="col-12 col-md-6" :class="{'col-lg-3': !isPatient, 'col-lg-4': isPatient}">
          <v-form-group :staticLabel="true" label="Status">
            <dt-filter-select id="status-filter"
                              column-name="Status"
                              :options="statusFilterOptions"
                              placeholder="Status of order"
                              multiple />
          </v-form-group>
        </div>
        <div class="col-12 col-md-6 col-lg-3" v-if="isAdmin">
          <v-form-group :staticLabel="true" label="Labwork Type">
            <dt-filter-select id="labwork-type-filter"
                              column-name="LabworkType"
                              add-empty
                              :options="labworkTypeFilterOptions"
                              placeholder="Labwork type of order" />
          </v-form-group>
        </div>
        <div class="col-12 col-md-6 col-lg-3" v-if="isAdmin || isClientAdmin">
          <v-form-group :staticLabel="true" label="Client">
            <dt-filter-select id="client-filter"
                              column-name="ClientId"
                              add-empty
                              :options="clientOptions"
                              placeholder="Name or ID" />
          </v-form-group>
        </div>
        <div class="col-12 col-md-6 col-lg-3" v-if="isEmployerTaggingAvailable">
          <v-form-group :staticLabel="true" label="Employer">
            <dt-filter-select id="employer-filter"
                              column-name="EmployerTag"
                              :options="tagOptions"
                              placeholder="Name or ID"
                              multiple />
          </v-form-group>
        </div>
        <div class="col-12" v-if="isAdmin">
          <v-form-group :staticLabel="true" label="Assigned">
            <dt-filter-checkbox id="assigned-to-me-filter"
                                column-name="AssigneeName">
              View Assigned to me Orders
            </dt-filter-checkbox>
          </v-form-group>
        </div>
        <div class="col-12" v-if="showReportVisibilityFeature">
          <v-form-group :staticLabel="true" label="Hidden">
            <dt-filter-checkbox id="show-hidden-orders-filter" column-name="Hidden" v-model="toggleVisibility">
              Also show hidden orders
            </dt-filter-checkbox>
          </v-form-group>
        </div>
      </div>
    </dt-filters-group>
    <dt-datatable ref="reportsList"
                  :id="tableId"
                  url="/report/report-list"
                  :columns="columns"
                  :dt-options="dtOptions">
      <template #type="{ data }">
        <span class="text-wrap">{{data.type}}</span>
      </template>

      <template #assignee-name="{ data }">
        <div 
            class="td-links-wrap" 
            v-if="
              data.status == reportStatus.WaitingReview ||
              data.status == reportStatus.WaitingResults ||
              data.status == reportStatus.WaitingSurvey ||
              data.status == reportStatus.OrderLabsFirst ||
              data.status == reportStatus.LabsWaitingSurvey ||
              data.status == reportStatus.OrderOnHold ||
              data.status == reportStatus.FinalProcessing
            "
        >
          <span class="td-text text-secondary" v-if="data.assigneeName">{{ data.assigneeName }}</span>
          <a class="p-0 btn btn-link text-success" v-if="!data.assigneeName" @click="assignReport(data.id)">Assign to me</a>
        </div>
      </template>

      <template #patient-name="{ data }">
        <div class="td-links-wrap">
          <span class="td-text text-success">{{ data.patientName }}</span>
        </div>
      </template>

      <template #status="{ data }">
        <report-status-patient @on-download-req="downloadReq" @on-download-report="downloadReport" v-if="isPatient" :data="data" />
        <report-status-client @on-download-req="downloadReq" @on-label-click="showActionModal" @on-download-report="downloadReport" @on-notify="showActionModal" @redraw="redrawTable" v-if="isClient || isClientAdmin" :data="data" />
        <report-status-admin @on-download-req="downloadReq" @on-label-click="showActionModal" @on-download-report="downloadReport" @on-notify="notify" @redraw="redrawTable" v-if="isAdmin" :data="data" />
      </template>

      <template #labwork-type="{ data }">
        <span>{{ data.labworkTypeName }}</span>
      </template>

      <template #actions="{ data }">
        <div class="td-links-wrap" v-if="isAdmin">
          <a class="p-0 btn btn-link text-success" @click="viewReport(data.id)">
            View
          </a>
          <a class="p-0 btn btn-link text-success ml-3" @click="loginAsPatient(data)" v-if="data.assigneeId === currentUserId">
            Login As
          </a>
        </div>
        <div class="td-links-wrap"
          v-if="(canCompletePatientSurvey && (data.status == reportStatus.WaitingSurvey || data.status == reportStatus.LabsWaitingSurvey)) || showReportVisibilityFeature">
          <a v-if="canCompletePatientSurvey && (data.status == reportStatus.WaitingSurvey || data.status == reportStatus.LabsWaitingSurvey)"
            :class="['p-0', 'btn btn-link', 'text-success', { 'mr-3': showReportVisibilityFeature && data.status == reportStatus.Completed }]"
            @click="showSurveyDialog(data)">
            Survey
          </a>
          <a class="p-0 btn btn-link text-success" v-if="showReportVisibilityFeature && data.status == reportStatus.Completed" @click="toggleReportVisibility(data.id)">
            <template v-if="data.hidden">Unhide</template>
            <template v-else>Hide</template>
          </a>
        </div>
      </template>
    </dt-datatable>

    <b-modal ref="surveyOptionDialog"
             cancel-variant="success"
             title="Survey Options"
             size="lg">
      Select which option you would like to use to take the survey from this session:
      <div>
        <b-btn variant="success" class="other-button mt-3" @click="completePatientSurvey">Complete the Patient Survey</b-btn>
        <b-btn variant="success" class="other-button my-3" @click="completePatientSurveyByPatient">Make Survey Available to the Individual To Take On This Device</b-btn>
      </div>
      <div>
        <b>Note:</b> to Protect your other patient's privacy, the latter option will
        require you to log back in after the survey is complete/saved
      </div>
      <template #modal-footer="{ cancel }">
        <b-button size="md" variant="success" @click="cancel()">
          Cancel
        </b-button>
      </template>
    </b-modal>

    <b-modal ref="clientSelect"
             cancel-variant="outline-secondary"
             ok-variant="success"
             title="Choose Client">
      <p>Select client for analysis order.</p>
      <v-form-group :validator="$v.selectedClient" label="Client">
        <v-select :options="clientOptions"
                  :clearable="false"
                  :filterable="false"
                  :searchable="false"
                  :multiple="false"
                  v-model="selectedClient">
        </v-select>
      </v-form-group>
      <template #modal-footer="{ ok, cancel }">
        <b-button size="md" variant="light" @click="cancel()">
          Go Back
        </b-button>
        <b-button size="md" variant="success" @click="submitClientSelect()">
          Submit
        </b-button>
      </template>
    </b-modal>

    <report-action-modal ref="reportActionModal" @cancel="redrawTable" />
  </div>
</template>

<script>
  import reportStatus from 'enums/report-status';
  import productType from 'enums/product-type';
  import LabworkType from 'enums/labwork-type';

  import reportService from 'services/report-service';
  import adminService from 'services/admin-service';
  import clientService from 'services/client-service';

  import ReportStatusPatient from './report-status-templates/report-status-patient';
  import ReportStatusClient from './report-status-templates/report-status-client';
  import ReportStatusAdmin from './report-status-templates/report-status-admin';
  import ReportActionModal from './report-action-modal';

  import { defaults } from "vue-datatables.net";
  import { required } from 'vuelidate/lib/validators';

  import { mapActions, mapState } from 'vuex';

  import {
    employerTagging,
    reportVisibility
  } from "@/constants";

  export default {
    name: 'ReportsList',
    components: {
      ReportStatusPatient,
      ReportStatusClient,
      ReportStatusAdmin,
      ReportActionModal
    },
    data() {
      return {
        tableId: `reports-list-id${this.$auth.id}`,
        dtOptions: {
          language: {
            ...defaults.language,
            emptyTable: this.isAdmin || this.isClientAdmin || this.isClient ? "You have no reports to view. Place an order above." : "You have no reports to view."
          },
          order: [[1, "desc"]]
        },
        typeFilterOptions: [
          { text: "Diabetic Risk Assessment", value: productType.InitialDRA },
          { text: "Follow-up Diabetic Risk Assessment", value: productType.RepeatedDRA },
          { text: "Cardiometabolic Risk Assessment", value: [productType.InitialCRALDLP, productType.InitialCRAAPOB] },
          { text: "Follow-up Cardiometabolic Risk Assessment", value: [productType.RepeatedCRALDLP, productType.RepeatedCRAAPOB] }
        ],
        labworkTypeFilterOptions: [
          { text: "Internal", value: LabworkType.Internal },
          { text: "By Client", value: LabworkType.ByClient },
          { text: "Client Draw", value: LabworkType.ClientDraw }
        ],
        statusOptions: {
          admin: [
            { text: "Waiting for Patient's Survey", value: `${reportStatus.WaitingSurvey};${reportStatus.LabsWaitingSurvey}` },
            { text: "Preparing Labwork Order", value: reportStatus.WaitingReview },
            { text: "Waiting for Labwork Results", value: `${reportStatus.WaitingResults};${reportStatus.FinalProcessing}` },
          ],
          patient: [
            { text: "Waiting for Patient's Survey", value: `${reportStatus.WaitingSurvey};${reportStatus.LabsWaitingSurvey}` },
            { text: "Preparing Labwork Order", value: `${reportStatus.WaitingReview};${reportStatus.WaitingResults}` },
          ],
          client: [
            { text: "Waiting for Patient's Survey", value: `${reportStatus.WaitingSurvey};${reportStatus.LabsWaitingSurvey}` },
            { text: "Preparing Labwork Order", value: reportStatus.WaitingReview },
            { text: "Waiting for Labwork Results", value: `${reportStatus.WaitingResults};${reportStatus.FinalProcessing}` },
          ]
        },
        clientOptions: [],
        tagOptions: [],
        reportStatus,
        reportsStats: null,

        LabworkType,

        selectedClient: null,
        selectedReport: null,
        toggleVisibility: 'false'
      }
    },

    validations: {
      selectedClient: {
        required
      }
    },

    computed: {
      ...mapState('client', ["clients"]),
      ...mapState('tag', ["tags"]),

      statusFilterOptions() {
        return [
          ...(this.isAdmin ? this.statusOptions.admin : []),
          ...(this.isPatient ? this.statusOptions.patient : []),
          ...(this.isClient || this.isClientAdmin ? this.statusOptions.client : []),
          { text: "Cancelled", value: reportStatus.Cancelled },
          { text: "Completed", value: reportStatus.Completed },
          { text: "Order Labs First", value: reportStatus.OrderLabsFirst },
          { text: "Order on Hold", value: reportStatus.OrderOnHold }
        ]
      },

      isAdmin() {
        return this.$auth.isInRole("Admin");
      },

      isClient() {
        return this.$auth.isInRole("Client") || this.$auth.isInRole("ClientUser") || this.$auth.isInRole("ClientOrderer") || this.$auth.isInRole("ClientProvider");
      },

      isPatient() {
        return this.$auth.isInRole("Patient");
      },

      isClientAdmin() {
        return this.$auth.isInRole("ClientAdmin");
      },

      canCompletePatientSurvey() {
        return this.isClient && !this.$auth.isInRole("ClientOrderer") || this.isClientAdmin
      },

      currentUserId() {
        return parseInt(this.$auth.userInfo.sub);
      },

      columns() {
        let columns = [
          { name: "Id", title: "Order ID", render: (data) => "#" + data },
          { name: "CompletionDate", title: "Completion Date" },
          { name: "CreationDate", title: "Date Of Order" },
          { name: "Type", title: "Type" },
          { name: "Status", title: "Status (Select for Options)" },
          { name: "ClientId", visible: false }
        ];

        if (!this.isPatient) {
          columns.unshift({ name: "PatientName", title: "Patient", sortable: true });
          columns.push({ name: "LabworkType", title: "Labwork Type" });
        }

        if (this.isAdmin) {
          columns.push({ name: "AssigneeName", title: "Assignee", sortable: true });
          columns.push({ name: "Actions", data: false });
        }

        if (this.canCompletePatientSurvey || this.showReportVisibilityFeature) {
          if (this.showReportVisibilityFeature) {
            columns.push({ name: "Hidden", visible: false });
          }

          columns.push({ name: "Actions", data: false });
        }

        if (this.isEmployerTaggingAvailable)
          columns.splice(columns.map(e => e.name).indexOf("Id") + 1, 0, { name: "EmployerTag", sortable: true });

        return columns;
      },

      isEmployerTaggingAvailable() {
        return this.$auth.isFeatureAvailable(employerTagging) || this.isAdmin;
      },

      isReportVisibilityAvailable() {
        return this.$auth.isFeatureAvailable(reportVisibility);
      },

      showReportVisibilityFeature() {
        return this.isReportVisibilityAvailable && this.isClient;
      }
    },

    methods: {
      ...mapActions('client', ["getClientsForFilter"]),
      ...mapActions('user', ["loginAsUser"]),
      ...mapActions('tag', ["getTagSelectList"]),

      async downloadReq(report) {
        await reportService.downloadReqDocument(report);
        this.$root.successToast("Lab requisition document downloaded!");
      },

      async loginAsPatient(data) {
        let user = {
          email: data.patientEmail,
        };

        await this.loginAsUser({
          user: user
        });
      },

      submitClientSelect() {
        if (this.$v.selectedClient.$invalid) {
          this.$v.selectedClient.$touch();

          return;
        }

        this.$router.push({ path: "reports/create", query: { id: this.selectedClient.value } });

        this.$refs.clientSelect.hide();
      },

      showActionModal(data) {
        this.$refs.reportActionModal.show(data);
      },

      showSurveyDialog(data) {
        this.assignReport(data.id);

        this.selectedReport = data;
        this.$refs.surveyOptionDialog.show();
      },

      async completePatientSurvey() {
        let user = {
          email: this.selectedReport.patientEmail,
        };

        await this.loginAsUser({
          user: user,
          clientRedirect: true
        });
      },

      async completePatientSurveyByPatient() {
        let user = {
          email: this.selectedReport.patientEmail,
        };

        await this.loginAsUser({
          user: user,
          clientRedirect: true,
          grantType: "client_as_patient"
        });
      },

      createReport() {
        if (this.isAdmin || this.isClientAdmin)
          this.$refs.clientSelect.show();
        else
          this.$router.push("reports/create");
      },

      async assignReport(id) {
        await reportService.assign(id);
        this.$root.successToast("Report assigned!");
        this.redrawTable();
      },

      async notify(data) {
        await reportService.notify(data.id);
        this.$root.successToast("Notification sent!");
      },

      async downloadReport(report) {
        await reportService.downloadReport(report);
        this.$root.successToast("Report downloaded!");
      },

      viewReport(id) {
        this.$router.push(`reports/${id}/view`);
      },

      async toggleReportVisibility(id) {
        await reportService.toggleReportVisibility(id);
        this.$root.successToast("Report visibility changed!");
        this.redrawTable();
      },

      redrawTable() {
        this.$refs.reportsList.draw(false);
      }
    },
	
    async created() {
      if (this.isPatient) {
        this.reportsStats = await reportService.getReportStats();
      }
    },

    async mounted() {
      if (this.isClient) {
        this.client = await clientService.getByUserId(parseInt(this.$auth.userInfo.sub));

        await this.getTagSelectList({ clientId: this.client.id, isForFilter: true });
        this.tagOptions = this.tags;
      }

      if (this.isAdmin) {
        await this.getClientsForFilter();

        await this.getTagSelectList({ isForFilter: true });
        this.tagOptions = this.tags;

        this.clientOptions = this.clients.sort((a, b) => a.label.localeCompare(b.label));
      }
      else if (this.isClientAdmin) {
        this.clientOptions = (await adminService.getClientsForClientAdmin()).map(c => {
          return {
            text: `${c.id} - ${c.name}`,
            label: `${c.id} - ${c.name}`,
            value: c.id
          }
        }).sort((a, b) => a.label.localeCompare(b.label));
      }

      // Force to update dynamic column localstorage,
      this.$refs.reportsList.api().state.clear();
    }
  };
</script>

<style lang="scss" scoped>
  .other-button {
    display: block;
    margin: auto;
    width: 21.5rem;
  }
</style>